import React, { FC } from 'react';
import DownloadControls from '../../../../common/download-controls/download-controls';
import { BUTTON_TYPES } from '../../../../constants/button-variants';
import translation from '../../../../languages';
import cn from '../../../../utils/classnames';
import isInteractiveStandaloneMaterial from '../../../../utils/interactive/is-interactive-standalone-material';
import { isFreeMaterial } from '../../../../utils/material/material-helper';
import { hasACL } from '../../../../utils/price/price-block.helpers';
import AddToCartButton from '../../../add-to-cart-button/add-to-cart-button';
import { CartIconName } from '../../../add-to-cart-button/add-to-cart-button.types';
import GetFreeMaterialButton from '../../../get-free-material-button/get-free-material-button';
import Icon from '../../../icon/icon';
import { toCartFromCardEventExtra } from '../../material-tile/bottom-part/bottom-part.helpers';
import './cart-download-btn.scss';

type CartDownloadBtnPropsType = TranslationProps & {
    type: 'icon' | 'button';
    item: Material;
    itemIndex?: number;
    showAddToCart?: boolean;
    showDownload?: boolean;
    addToCartButtonClassNames?: string;
    cartIconName?: CartIconName;
    onAddToCartSuccess?: (props: { cart: Cart; idsOfMaterialsIncludedInBundle: number[] }) => void;
    onAddToCartError?: VoidFunction;
    isAddToCartButtonDisabled?: boolean;
    shouldPreventCartOpen?: boolean;
    isAddToCartButtonLoading?: boolean;
    onAddToCartCallback?: (material: Material) => boolean;
    wrapperClassName?: string;
    hideRestrictionsMessage?: boolean;
    searchRequestId?: string;
};

const CartDownloadBtn: FC<CartDownloadBtnPropsType> = (props) => {
    const {
        type,
        item,
        addToCartButtonClassNames,
        itemIndex,
        onAddToCartSuccess,
        onAddToCartError,
        onAddToCartCallback,
        isAddToCartButtonDisabled,
        shouldPreventCartOpen,
        isAddToCartButtonLoading,
        showAddToCart,
        showDownload,
        cartIconName,
        wrapperClassName,
        hideRestrictionsMessage,
        searchRequestId,
        t
    } = props;
    const isFree = isFreeMaterial(item);
    const hasACLAvailable = item.hasAccess || hasACL(item) || isFree;
    const showDownloadBtn = showDownload && hasACLAvailable && !isInteractiveStandaloneMaterial(item);

    const showAddToCartBtn = showAddToCart && !showDownloadBtn && !isAddToCartButtonLoading && !hasACLAvailable;

    if (!showDownloadBtn && !showAddToCartBtn) {
        return null;
    }

    if (type === 'button') {
        return (
            <div className={cn('cart-download-btn', wrapperClassName)}>
                {showDownloadBtn && (
                    <>
                        {isFree && !hasACL(item) ? (
                            <GetFreeMaterialButton
                                material={item}
                                type={BUTTON_TYPES.BUTTON}
                                // @ts-ignore -- 'download-btn' is a CSS-only variant, not in BUTTON_VARIANTS
                                variant='download-btn'
                                searchRequestId={searchRequestId}
                            >
                                <Icon
                                    animated={false}
                                    height={24}
                                    name='download'
                                    text={t('common.materialBlock.download2')}
                                />
                            </GetFreeMaterialButton>
                        ) : (
                            <DownloadControls material={item} disableScrollToTop searchRequestId={searchRequestId} />
                        )}
                    </>
                )}
                {showAddToCartBtn && (
                    <AddToCartButton
                        className={cn('styled', addToCartButtonClassNames, {
                            yellow: !addToCartButtonClassNames
                        })}
                        type='button'
                        material={item}
                        materialIndex={itemIndex}
                        onAddToCartCallback={onAddToCartCallback}
                        onAddToCartSuccess={onAddToCartSuccess}
                        onAddToCartError={onAddToCartError}
                        shouldPreventCartOpen={shouldPreventCartOpen}
                        isDisabled={isAddToCartButtonDisabled}
                        hideRestrictionsMessage={hideRestrictionsMessage}
                        qualityEventsExtra={toCartFromCardEventExtra()}
                        searchRequestId={searchRequestId}
                    />
                )}
            </div>
        );
    }

    return (
        <div className={cn('cart-download-icon-btn', wrapperClassName)}>
            {showDownloadBtn && (
                <>
                    {isFree && !hasACL(item) ? (
                        <GetFreeMaterialButton
                            material={item}
                            iconType='download'
                            type={BUTTON_TYPES.ICON}
                            searchRequestId={searchRequestId}
                        />
                    ) : (
                        <DownloadControls
                            material={item}
                            disableScrollToTop
                            type={BUTTON_TYPES.ICON}
                            searchRequestId={searchRequestId}
                        />
                    )}
                </>
            )}
            {showAddToCartBtn && (
                <AddToCartButton
                    material={item}
                    type='icon'
                    materialIndex={itemIndex}
                    cartIconName={cartIconName}
                    onAddToCartCallback={onAddToCartCallback}
                    onAddToCartSuccess={onAddToCartSuccess}
                    qualityEventsExtra={toCartFromCardEventExtra()}
                    isDisabled={isAddToCartButtonDisabled}
                    searchRequestId={searchRequestId}
                />
            )}
        </div>
    );
};

const translate = translation(['common']);

export default translate(CartDownloadBtn);
